import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {IPairedExerciseForm} from "../../../shared/interfaces/forms/exercises/IPairedExerciseForm";
import {RegularExerciseFormFactory} from "./RegularExerciseFormFactory";
import {IRegularExerciseForm} from "../../../shared/interfaces/forms/exercises/IRegularExerciseForm";
import {FormHelper} from "../../helpers/form-helper";
import {ICategory} from "../../../shared/interfaces/ICategory";

type PairedConfigControlName =
  | "countForm"
  | "reactForm"
  | "sequenceForm"
  | "agilityForm"
  | "verticalForm";

export class PairedExerciseFormFactory {
  static create(): FormGroup<IPairedExerciseForm> {
    const categoryControl = new FormControl(
      null,
      [Validators.required],
    );

    const typeControl = new FormControl(0, [Validators.required]);
    const firstExerciseForm = RegularExerciseFormFactory.create();
    firstExerciseForm.setControl(
      "category",
      FormHelper.clone(categoryControl) as FormControl<ICategory>,
    );
    firstExerciseForm.setControl(
      "type",
      FormHelper.clone(typeControl) as FormControl<number>,
    );

    const secondExerciseForm = PairedExerciseFormFactory.siblingForm(
      firstExerciseForm,
      typeControl,
      categoryControl,
    );

    return new FormGroup<IPairedExerciseForm>({
      name: new FormControl("", [Validators.required]),
      category: categoryControl,
      type: typeControl,
      firstExercise: firstExerciseForm,
      secondExercise: secondExerciseForm,
    });
  }

  static siblingForm(
    firstExerciseForm: FormGroup<IRegularExerciseForm>,
    typeControl: FormControl<number>,
    categoryControl: FormControl<ICategory>,
  ): FormGroup<IRegularExerciseForm> {
    const sibling = RegularExerciseFormFactory.create();
    sibling.setControl(
      "category",
      FormHelper.clone(categoryControl) as FormControl<ICategory>,
    );
    sibling.setControl(
      "type",
      FormHelper.clone(typeControl) as FormControl<number>,
    );

    const firstConfig = firstExerciseForm.controls.config;
    const siblingConfig = sibling.controls.config;

    siblingConfig.setControl(
      "difficulty",
      FormHelper.clone(firstConfig.controls.difficulty) as FormControl<number>,
    );

    const firstCountdown = firstConfig.controls.countdownForm;
    const siblingCountdown = siblingConfig.controls.countdownForm;
    siblingCountdown.setControl(
      "startType",
      FormHelper.clone(firstCountdown.controls.startType) as FormControl<number>,
    );
    siblingCountdown.setControl(
      "startValue",
      FormHelper.clone(firstCountdown.controls.startValue) as FormControl<number>,
    );
    siblingCountdown.setControl(
      "endType",
      FormHelper.clone(firstCountdown.controls.endType) as FormControl<number>,
    );
    siblingCountdown.setControl(
      "endValue",
      FormHelper.clone(firstCountdown.controls.endValue) as FormControl<number>,
    );

    siblingConfig.controls.countForm.setControl(
      "countType",
      FormHelper.clone(
        firstConfig.controls.countForm.controls.countType,
      ) as FormControl<number>,
    );

    const firstReact = firstConfig.controls.reactForm;
    const siblingReact = siblingConfig.controls.reactForm;
    siblingReact.setControl(
      "delay",
      FormHelper.clone(firstReact.controls.delay) as FormControl<number>,
    );
    siblingReact.setControl(
      "timerPrompt",
      FormHelper.clone(firstReact.controls.timerPrompt) as FormControl<number>,
    );
    siblingReact.setControl(
      "neurocognitiveType",
      FormHelper.clone(
        firstReact.controls.neurocognitiveType,
      ) as FormControl<number>,
    );
    siblingReact.setControl(
      "stroopEffectType",
      FormHelper.clone(
        firstReact.controls.stroopEffectType,
      ) as typeof siblingReact.controls.stroopEffectType,
    );
    siblingReact.setControl(
      "liftTime",
      FormHelper.clone(firstReact.controls.liftTime) as FormControl<boolean>,
    );
    siblingReact.setControl(
      "footFireActivity",
      FormHelper.clone(
        firstReact.controls.footFireActivity,
      ) as FormControl<boolean>,
    );
    siblingReact.setControl(
      "reactTo",
      FormHelper.clone(
        firstReact.controls.reactTo,
      ) as FormArray<FormControl<boolean>>,
    );
    siblingReact.setControl(
      "dontReactTo",
      FormHelper.clone(
        firstReact.controls.dontReactTo,
      ) as FormArray<FormControl<boolean>>,
    );

    const firstSequence = firstConfig.controls.sequenceForm;
    const siblingSequence = siblingConfig.controls.sequenceForm;
    siblingSequence.setControl(
      "delay",
      FormHelper.clone(firstSequence.controls.delay) as FormControl<number>,
    );
    siblingSequence.setControl(
      "timerPrompt",
      FormHelper.clone(
        firstSequence.controls.timerPrompt,
      ) as FormControl<number>,
    );

    const firstAgility = firstConfig.controls.agilityForm;
    const siblingAgility = siblingConfig.controls.agilityForm;
    siblingAgility.setControl(
      "delay",
      FormHelper.clone(firstAgility.controls.delay) as FormControl<number>,
    );
    siblingAgility.setControl(
      "timerPrompt",
      FormHelper.clone(
        firstAgility.controls.timerPrompt,
      ) as FormControl<number>,
    );

    const firstVertical = firstConfig.controls.verticalForm;
    const siblingVertical = siblingConfig.controls.verticalForm;
    siblingVertical.setControl(
      "verticalJumpType",
      FormHelper.clone(
        firstVertical.controls.verticalJumpType,
      ) as typeof siblingVertical.controls.verticalJumpType,
    );
    siblingVertical.setControl(
      "numberOfLegs",
      FormHelper.clone(
        firstVertical.controls.numberOfLegs,
      ) as FormControl<number>,
    );
    siblingVertical.setControl(
      "numberOfJumps",
      FormHelper.clone(
        firstVertical.controls.numberOfJumps,
      ) as FormControl<number>,
    );
    siblingVertical.setControl(
      "isVerticalReact",
      FormHelper.clone(
        firstVertical.controls.isVerticalReact,
      ) as FormControl<boolean>,
    );
    siblingVertical.setControl(
      "reactTo",
      FormHelper.clone(
        firstVertical.controls.reactTo,
      ) as FormArray<FormControl<boolean>>,
    );
    siblingVertical.setControl(
      "dontReactTo",
      FormHelper.clone(
        firstVertical.controls.dontReactTo,
      ) as FormArray<FormControl<boolean>>,
    );

    return sibling;
  }

  static resetTypeConfiguration(
    formGroup: FormGroup<IPairedExerciseForm>,
    type: number,
  ): boolean {
    const controlName = PairedExerciseFormFactory.configControlName(type);
    if (!controlName) return false;

    const defaults = PairedExerciseFormFactory.create();
    const firstConfig = formGroup.controls.firstExercise.controls.config;
    const secondConfig = formGroup.controls.secondExercise.controls.config;
    const firstDefaults = defaults.controls.firstExercise.controls.config;
    const secondDefaults = defaults.controls.secondExercise.controls.config;

    firstConfig.setControl("difficulty", firstDefaults.controls.difficulty);
    secondConfig.setControl("difficulty", secondDefaults.controls.difficulty);
    firstConfig.setControl(
      "countdownForm",
      firstDefaults.controls.countdownForm,
    );
    secondConfig.setControl(
      "countdownForm",
      secondDefaults.controls.countdownForm,
    );
    firstConfig.setControl(controlName, firstDefaults.controls[controlName]);
    secondConfig.setControl(controlName, secondDefaults.controls[controlName]);
    return true;
  }

  private static configControlName(
    type: number,
  ): PairedConfigControlName | undefined {
    return (
      [
        "countForm",
        "reactForm",
        "sequenceForm",
        "agilityForm",
        "verticalForm",
      ] as const
    )[type];
  }
}
